import { deviceSession } from '@/privacy/deviceStorage';
import type { CSSProperties } from 'react';
import {
  PAGE_DETAIL_WIDTH_DEFAULT,
  PAGE_DETAIL_WIDTH_MAX,
  PAGE_DETAIL_WIDTH_MIN,
} from '@featit/shared/page-column-widths';

/**
 * The one width rule for a resizable detail panel beside a list — the work-item list's panel, and
 * the `ResizableDetailPanel` Views and the roadmap share (I-0834, I-0835).
 *
 * The reader chooses a width; the panel draws it when there is room and gives way when there is
 * not, down to its own floor, so the column beside it keeps at least `LIST_MIN_WIDTH_BESIDE_PANEL`.
 * The browser does that arithmetic (`.detail-split-panel` in `index.css`), with whatever else the
 * split holds — a view's catalog, the gaps — so the first frame is already the final one. Measured
 * in a script instead, the panel drew the reader's full 480 px until the first ResizeObserver
 * report, and a busy machine held that frame long enough to catch: a 212 px list at 768 (gate 280).
 *
 * ⛔ The two floors are stated in `index.css` too — CSS cannot read this module — and
 * `detailPanelWidth.guard.test.ts` fails when the two disagree.
 */
// The store's own bounds (F-0793, L-41): a width the server keeps is the width the handle allows.
export const DETAIL_PANEL_MIN_WIDTH = PAGE_DETAIL_WIDTH_MIN;
export const DETAIL_PANEL_MAX_WIDTH = PAGE_DETAIL_WIDTH_MAX;
export const DETAIL_PANEL_DEFAULT_WIDTH = PAGE_DETAIL_WIDTH_DEFAULT;
/** What the column beside an open panel keeps, so a portrait iPad still reads it (AC-6.7). */
export const LIST_MIN_WIDTH_BESIDE_PANEL = 320;
/** The resize handle between the two, with its margins. */
export const DETAIL_HANDLE_WIDTH = 24;
/**
 * Shared by Views' and the roadmap's panels, so a width chosen in one is the width the next opens at.
 * Work items' own pane keeps the reader's width with them instead (F-0793, L-45).
 */
export const DETAIL_WIDTH_SESSION_KEY = 'featit:workItemsDetailWidth';
/** The class that puts a panel under the rule. */
export const DETAIL_PANEL_CLASS = 'detail-split-panel';

export function clampDetailWidth(width: number): number {
  return Math.min(DETAIL_PANEL_MAX_WIDTH, Math.max(DETAIL_PANEL_MIN_WIDTH, Math.round(width)));
}

/**
 * The width the reader chose in this session, or null when they have not: the pane then opens at
 * its view's default (`--detail-panel-default`, the Activity card's `minmax(25rem, 34vw)`) or 480.
 */
export function chosenDetailWidth(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = deviceSession.getItem(DETAIL_WIDTH_SESSION_KEY);
  const stored = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(stored) ? clampDetailWidth(stored) : null;
}

/** The reader's width, handed to the rule; the rule decides what is drawn. */
export function detailPanelStyle(width: number): CSSProperties {
  return { '--detail-panel-width': `${width}px` } as CSSProperties;
}
